<script lang="ts">
    import * as Sidebar from "../ui/sidebar/index.js";
    import ChartPie from "@lucide/svelte/icons/chart-pie";
    import InboxIcon from "@lucide/svelte/icons/inbox";
    import Settings from "@lucide/svelte/icons/settings";
    import Squirrel from "@lucide/svelte/icons/squirrel";
    import * as DropdownMenu from "../ui/dropdown-menu/index.js";
    import { locale, locales } from "../../locale.svelte.js";
    import { useURL } from "../../url.svelte.js";
    import { useLocalStorage } from "../../local-storage.svelte.js";
    import type { Component, Snippet } from "svelte";
    import OrganizationSelect from "../organization-select.svelte";
    import ChevronsUpDownIcon from "@lucide/svelte/icons/chevrons-up-down";
    import * as Avatar from "../ui/avatar/index.js";
    import LogOutIcon from "@lucide/svelte/icons/log-out";
    import GlobeIcon from "@lucide/svelte/icons/globe";
    import PaletteIcon from "@lucide/svelte/icons/palette";
    import { userPrefersMode } from "mode-watcher";
    import icon from "../../../assets/icon-192x192.png";
    import { resolve, useRoute } from "../../route.svelte.js";
    import Breadcrumbs from "../breadcrumbs.svelte";
    import { Separator } from "../ui/separator";
    import { auth, signOut, useOrganization, useSession } from "../../auth.svelte.js";
    import { z, firstOrDie } from "@repo/utils";
    import { m } from "../../messages.svelte.js";
    import Boundary from "../common/boundary.svelte";

    const { children }: { children: Snippet } = $props();

    const organization = useOrganization();
    const session = useSession();
    const organizations = auth.useListOrganizations();
    const multiOrg = $derived($organizations.data && $organizations.data.length > 1);
    const ls = useLocalStorage("sidebar", { open: z.boolean() });
    const workbenchSubItems = $derived([
        { title: m["myTasks"](), query: { view: "my" } },
        { title: m["cases"](), query: { view: organization.metadata["useCase"] === "SO" ? "so" : "oc" } },
        { title: m["all"](), query: { view: "all" } },
    ]);
    const sidebarItems = $derived(
        (
            [
                { title: m["dashboard_"](), routes: ["/"], icon: ChartPie },
                {
                    title: m["workbench_"](),
                    routes: ["/workbench", "/workbench/:caseId"],
                    icon: Squirrel,
                    subItems: workbenchSubItems,
                },
                { title: m["mailbox"](), routes: ["/admin/mailbox"], icon: InboxIcon },
                { title: m["settings"](), routes: ["/settings"], icon: Settings },
            ] satisfies {
                title: string;
                routes: string[];
                icon: Component;
                subItems?: { title: string; query: Record<string, string> }[];
            }[]
        ).filter((x) => session.user.role === "admin" || x.routes.every((y) => !y.includes("admin"))),
    );
    const url = useURL();
    const route = useRoute();
</script>

<Sidebar.Provider bind:open={() => ls.open ?? true, (x) => (ls.open = x)} class="overflow-hidden">
    <Sidebar.Root collapsible="icon" variant="sidebar">
        <Sidebar.Header>
            <Sidebar.Menu>
                <Sidebar.MenuItem>
                    {#if multiOrg}
                        <OrganizationSelect>
                            {#snippet child({ props })}
                                <Sidebar.MenuButton
                                    {...props}
                                    size="lg"
                                    class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                                >
                                    <div
                                        class="bg-sidebar text-sidebar-primary-foreground flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-md"
                                    >
                                        <enhanced:img src={organization.logo ?? icon} alt="" />
                                    </div>
                                    <div class="grid flex-1 text-left text-sm leading-tight">
                                        <span class="truncate font-medium">{organization.name}</span>
                                    </div>
                                    <ChevronsUpDownIcon class="ml-auto" />
                                </Sidebar.MenuButton>
                            {/snippet}
                        </OrganizationSelect>
                    {:else}
                        <Sidebar.MenuButton size="lg">
                            <div
                                class="bg-sidebar text-sidebar-primary-foreground flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-md"
                            >
                                <enhanced:img src={organization.logo ?? icon} alt="" />
                            </div>
                            <div class="grid flex-1 text-left text-sm leading-tight">
                                <span class="truncate font-medium">{organization.name}</span>
                            </div>
                        </Sidebar.MenuButton>
                    {/if}
                </Sidebar.MenuItem>
            </Sidebar.Menu>
        </Sidebar.Header>
        <Sidebar.Content>
            <Sidebar.Group>
                <Sidebar.Menu>
                    {#each sidebarItems as item (item.title)}
                        <Sidebar.MenuItem>
                            <Sidebar.MenuButton
                                tooltipContent={item.title}
                                isActive={!!route.current && item.routes.includes(route.current.route)}
                            >
                                {#snippet child({ props })}
                                    <a {...props} href={resolve(firstOrDie(item.routes))}>
                                        <item.icon />
                                        <span>{item.title}</span>
                                    </a>
                                {/snippet}
                            </Sidebar.MenuButton>
                            <Sidebar.MenuSub>
                                {#each item.subItems ?? [] as subItem (subItem.title)}
                                    <Sidebar.MenuSubItem>
                                        <Sidebar.MenuSubButton
                                            isActive={Object.entries(subItem.query).every(
                                                ([k, v]) => url.current.searchParams.get(k) === v,
                                            )}
                                        >
                                            {#snippet child({ props })}
                                                <a
                                                    {...props}
                                                    href={`${resolve(firstOrDie(item.routes))}?${new URLSearchParams(subItem.query)}`}
                                                >
                                                    <span>{subItem.title}</span>
                                                </a>
                                            {/snippet}
                                        </Sidebar.MenuSubButton>
                                    </Sidebar.MenuSubItem>
                                {/each}
                            </Sidebar.MenuSub>
                        </Sidebar.MenuItem>
                    {/each}
                </Sidebar.Menu>
            </Sidebar.Group>
        </Sidebar.Content>
        <Sidebar.Footer>
            <Sidebar.Menu>
                <Sidebar.MenuItem>
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                            {#snippet child({ props })}
                                <Sidebar.MenuButton
                                    size="lg"
                                    class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                                    {...props}
                                >
                                    {@render profile()}
                                    <ChevronsUpDownIcon class="ml-auto size-4" />
                                </Sidebar.MenuButton>
                            {/snippet}
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content
                            class="w-(--bits-dropdown-menu-anchor-width) min-w-56 rounded-lg"
                            side="right"
                            align="end"
                            sideOffset={4}
                        >
                            <DropdownMenu.Label class="p-0 font-normal">
                                <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                    {@render profile()}
                                </div>
                            </DropdownMenu.Label>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Group>
                                <DropdownMenu.Sub>
                                    <DropdownMenu.SubTrigger>
                                        <GlobeIcon />
                                        {m["locale"]()}
                                    </DropdownMenu.SubTrigger>
                                    <DropdownMenu.SubContent>
                                        <DropdownMenu.RadioGroup bind:value={locale.current}>
                                            {#each locales as x (x)}
                                                <DropdownMenu.RadioItem value={x}>{x}</DropdownMenu.RadioItem>
                                            {/each}
                                        </DropdownMenu.RadioGroup>
                                    </DropdownMenu.SubContent>
                                </DropdownMenu.Sub>
                                <DropdownMenu.Sub>
                                    <DropdownMenu.SubTrigger>
                                        <PaletteIcon />
                                        {m["theme"]()}
                                    </DropdownMenu.SubTrigger>
                                    <DropdownMenu.SubContent>
                                        <DropdownMenu.RadioGroup bind:value={userPrefersMode.current}>
                                            <DropdownMenu.RadioItem value="light">{m["light"]()}</DropdownMenu.RadioItem
                                            >
                                            <DropdownMenu.RadioItem value="dark">{m["dark"]()}</DropdownMenu.RadioItem>
                                            <DropdownMenu.RadioItem value="system"
                                                >{m["system"]()}</DropdownMenu.RadioItem
                                            >
                                        </DropdownMenu.RadioGroup>
                                    </DropdownMenu.SubContent>
                                </DropdownMenu.Sub>
                            </DropdownMenu.Group>
                            <DropdownMenu.Separator />
                            <DropdownMenu.Item onclick={signOut}>
                                <LogOutIcon />
                                {m["logOut"]()}
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </Sidebar.MenuItem>
            </Sidebar.Menu>
        </Sidebar.Footer>
        <Sidebar.Rail />
    </Sidebar.Root>
    <div class="flex h-screen grow flex-col overflow-auto">
        <header
            class="flex h-16 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12"
        >
            <div class="flex items-center gap-2 px-4">
                <Sidebar.Trigger class="-ml-1 size-8" />
                <Separator orientation="vertical" class="mr-2 data-[orientation=vertical]:h-4" />
                <Breadcrumbs />
            </div>
        </header>
        <Boundary>
            {@render children()}
        </Boundary>
    </div>
</Sidebar.Provider>

{#snippet profile()}
    <Avatar.Root class="size-8 rounded-lg">
        <Avatar.Image src={session.user.image} alt={session.user.name} />
        <Avatar.Fallback class="rounded-lg">
            {[session.user.firstName, session.user.lastName].map((x) => x[0]).join("")}
        </Avatar.Fallback>
    </Avatar.Root>
    <div class="grid flex-1 text-left text-sm leading-tight">
        <span class="truncate font-medium">{session.user.name}</span>
        <span class="truncate text-xs">{session.user.email}</span>
    </div>
{/snippet}
